// ============================================================================
// OFFICE FINANCE REPOSITORY
// ARQUIVO: src/services/OfficeFinanceRepository.js
// RESPONSABILIDADE: CRUD de lançamentos financeiros do escritório (ledger, templates, settings).
// Criado: Fase 6.3 — mover Firestore direto de OfficeFinance.jsx para repositório.
// ============================================================================

import {
  collection, collectionGroup, getDocs, query, orderBy, deleteDoc,
  doc, writeBatch, where, setDoc, getDoc, updateDoc
} from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';

const APP_ID = FIREBASE_CONFIG?.projectId || 'portalrda-f312d';

const PATH_LEDGER = ['artifacts', APP_ID, 'public', 'data', 'financial_ledger'];
const PATH_TEMPLATES = ['artifacts', APP_ID, 'public', 'data', 'office_recurrence_templates'];
const PATH_SETTINGS = ['artifacts', APP_ID, 'public', 'data', 'office_finance_settings', 'config'];

/** Carrega configurações do escritório. */
export async function loadOfficeFinanceSettings() {
  try {
    if (!db) return null;
    const docRef = doc(db, ...PATH_SETTINGS);
    const snap = await getDoc(docRef);
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    console.log('OfficeFinanceRepository: settings padrão');
    return null;
  }
}

/** Salva categorias. */
export async function saveOfficeCategories(newCats) {
  const docRef = doc(db, ...PATH_SETTINGS);
  await setDoc(docRef, { categories: newCats }, { merge: true });
}

/** Salva saldo inicial. */
export async function saveOfficeInitialBalance(newBalance) {
  const docRef = doc(db, ...PATH_SETTINGS);
  await setDoc(docRef, { initialBalance: Number(newBalance) }, { merge: true });
}

/** Carrega templates de recorrência do escritório. */
export async function loadOfficeTemplates() {
  const ref = collection(db, ...PATH_TEMPLATES);
  const snap = await getDocs(query(ref));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Carrega lançamentos do mês. */
export async function loadOfficePostingsByMonth(year, month, filterMode = 'DATE') {
  const startOfMonth = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const endOfMonth = `${year}-${String(month + 2).padStart(2, '0')}-00`;
  const queryField = filterMode === 'COMPETENCE' ? 'competenceDate' : 'date';
  const ref = collection(db, ...PATH_LEDGER);
  const q = query(ref, where(queryField, '>=', startOfMonth), where(queryField, '<=', endOfMonth), orderBy(queryField, 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Carrega lançamentos do ano. */
export async function loadOfficePostingsByYear(year, filterMode = 'DATE', yearViewMode = 'ALL') {
  const startOfYear = `${year}-01-01`;
  const endOfYear = `${year}-12-31`;
  const queryField = filterMode === 'COMPETENCE' ? 'competenceDate' : 'date';
  const ref = collection(db, ...PATH_LEDGER);
  const q = query(ref, where(queryField, '>=', startOfYear), where(queryField, '<=', endOfYear));
  const snap = await getDocs(q);
  const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  return yearViewMode === 'PAID_ONLY' ? data.filter(d => d.status === 'PAID') : data;
}

/** Busca postings por clientId ou linkedName (auditoria de saldo). */
export async function findOfficePostingsByClient(clientId, linkedName) {
  const ref = collection(db, ...PATH_LEDGER);
  let snap = null;
  if (clientId) {
    try { snap = await getDocs(query(ref, where('clientId', '==', clientId))); } catch (e) { console.error(e); }
  }
  if (!snap || !snap.docs || snap.docs.length === 0) {
    try { snap = await getDocs(query(ref, where('linkedName', '==', linkedName))); } catch (e) { console.error(e); }
  }
  return snap ? snap.docs.map(d => ({ id: d.id, ...d.data() })) : [];
}

/** Atualiza um lançamento existente. */
export async function updateOfficePosting(id, data) {
  const docRef = doc(db, ...PATH_LEDGER, id);
  const cleanAmount = Math.round(Number(data.amount) * 100) / 100;
  const status = data.isPaid ? 'PAID' : 'PENDING';
  await updateDoc(docRef, { ...data, value: cleanAmount, status });
}

/** Cria template de recorrência do escritório. */
export async function createOfficeTemplate(formData) {
  const batch = writeBatch(db);
  const templatesRef = collection(db, ...PATH_TEMPLATES);
  const newTplRef = doc(templatesRef);
  const cleanAmount = Math.round(Number(formData.amount) * 100) / 100;
  batch.set(newTplRef, {
    description: formData.description,
    amount: cleanAmount,
    group: formData.type,
    category: formData.subCategory,
    anchorDay: Number(formData.anchorDay),
    active: true,
    account: formData.account,
    createdAt: new Date().toISOString(),
  });
  await batch.commit();
}

/** Cria lançamento(s) no ledger do escritório. */
export async function createOfficePosting(formData, mode) {
  const totalAmount = Math.round(Number(formData.amount) * 100) / 100;
  const count = mode === 'SINGLE' ? 1 : formData.repeatCount;
  const valuePerInstallment = Math.round((totalAmount / count) * 100) / 100;

  for (let i = 0; i < count; i++) {
    const dueDate = new Date(formData.date);
    dueDate.setMonth(dueDate.getMonth() + i);
    let compDateStr = formData.competenceDate ? `${formData.competenceDate}-01` : dueDate.toISOString().split('T')[0];
    if (mode === 'INSTALLMENT') {
      const compDate = new Date(formData.date);
      compDate.setMonth(compDate.getMonth() + i);
      compDateStr = compDate.toISOString().split('T')[0];
    }
    let desc = formData.description;
    if (mode === 'INSTALLMENT') desc += ` (${i + 1}/${count})`;
    const isPaid = formData.isPaid && i === 0;
    const parcelaAuto = mode === 'INSTALLMENT' ? `${i + 1}/${count}` : (formData.parcela || null);

    const draft = {
      description: desc,
      value: valuePerInstallment,
      date: dueDate.toISOString().split('T')[0],
      competenceDate: compDateStr,
      settlementDate: isPaid ? new Date().toISOString().split('T')[0] : null,
      recorrencia: formData.recorrencia || 'Única',
      parcela: parcelaAuto,
      group: formData.type,
      category: formData.subCategory,
      account: formData.account,
      status: isPaid ? 'PAID' : 'PENDING',
      movementType: formData.movementType,
      linkedName: formData.linkedName,
      linkedToType: formData.linkedToType,
      linkedToId: formData.linkedToId,
      clientId: formData.clientId,
      wallet: formData.wallet,
      notes: formData.notes,
      isPaid: formData.isPaid,
    };
    const ref = collection(db, ...PATH_LEDGER);
    await setDoc(doc(ref), draft);
  }
}

/** Deleta lançamento ou template do escritório. */
export async function deleteOfficePosting(id, isTemplate = false) {
  const ref = isTemplate ? doc(db, ...PATH_TEMPLATES, id) : doc(db, ...PATH_LEDGER, id);
  await deleteDoc(ref);
}

/** Marca lançamento como pago. */
export async function markOfficePostingPaid(id, settlementDate) {
  const effectiveDate = settlementDate || new Date().toISOString().split('T')[0];
  const docRef = doc(db, ...PATH_LEDGER, id);
  await updateDoc(docRef, { status: 'PAID', settlementDate: effectiveDate });
}

/** Busca clientes para dropdown de vínculo. */
export async function fetchClientsForDropdown() {
  const clientQ = query(collectionGroup(db, 'profile'));
  const clientSnap = await getDocs(clientQ);
  return clientSnap.docs.map((d) => ({
    id: d.id,
    name: d.data().qualification?.physical?.name || d.data().qualification?.legal?.companyName || 'Cliente',
  }));
}

/** Busca processos (autos_workspaces) para dropdown de vínculo.
 * Fusão Processos/Caderno (MC34): trocado de collectionGroup(db,'processes')
 * para query direta em autos_workspaces — collectionGroup casava, sem
 * querer, com users/{uid}/processes (ProcessRepository.js, estrutura
 * diferente, fora de escopo desta fusão) só por terem o mesmo nome de
 * coleção em profundidades diferentes. parteContraria não existe como
 * campo flat no novo schema — usa o primeiro nome de partes.adversas[],
 * se houver, senão o alias do caderno. */
export async function fetchProcessesForDropdown() {
  const procQ = query(collection(db, 'artifacts', APP_ID, 'public', 'data', 'autos_workspaces'));
  const procSnap = await getDocs(procQ);
  return procSnap.docs.map((d) => {
    const data = d.data();
    const primeiraAdversa = Array.isArray(data?.partes?.adversas) ? data.partes.adversas[0]?.nome : '';
    return {
      id: d.id,
      label: `${data.numeroCNJ || '(sem CNJ)'} - ${primeiraAdversa || data.alias || ''}`.trim(),
    };
  });
}
